import { ThemedHeader } from '@bygghemmait/themified-components';
import logoBlackFriday from './bhlogo_svart.svg';
import bnoLogoBlackFriday from './bnologo_svart.svg';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { getCountDownAd } from 'components/CountdownAd/selector';
import {
  selectBlackFridayStatus,
  selectContactSection,
  selectChannel,
  selectLogoUrl,
  selectSiteMode,
  selectStripeLinks
} from 'components/Header/themes/BGH/selector';
import { useBridgeEvents } from 'experiments/variants/GecpHeader/useBridgeEvents';
import { SmallLogo } from 'components/Header/themes/BGH/smallLogo';
import { CompactLogoBlackFriday } from 'components/Header/themes/BGH/compactLogoBlackFriday';
import { CompactLogo } from 'components/Header/themes/BGH/compactLogo';
import { SmallLogoBlackFriday } from 'components/Header/themes/BGH/smallLogoBlackFriday';
import logoGOP from 'assets/img/golvpoolen/logo.svg';
import logoCompanyGOP from 'assets/img/golvpoolen/logo_company.svg';
import { SiteMode } from 'models/SiteMode';
import { CompanyLogoSmall } from 'components/Header/themes/GOP/companyLogoSmall';
import { MenuSiteModeBlock, SiteModeButton } from '@bygghemmait/gop/blocks';
import { hasElements } from 'utils';
import experiment from 'experiments/stripeLink.experiment';
import { useExperiment } from 'experiments/experiment';

const Container = styled.section`
  a,
  a:hover {
    text-decoration: none;
  }
`;

type WrapperProps = {
  $pp?: boolean;
};

const Wrapper = styled('div')<WrapperProps>(({ theme, $pp }) => ({
  display: 'flex',
  alignItems: 'center',
  width: $pp ? theme.spacing(36) : theme.spacing(34),

  [theme.breakpoints.up('md')]: {
    padding: 0,
    width: theme.spacing(75)
  }
}));

type CompactWrapperProps = {
  $bgh?: boolean;
};

const CompactWrapper = styled('div')<CompactWrapperProps>(({ theme, $bgh }) => ({
  display: 'flex',
  alignItems: 'center',
  width: $bgh ? theme.spacing(12) : undefined
}));

const StyledLogo = styled('img')({
  width: '100%'
});

export default (): JSX.Element => {
  useBridgeEvents();

  const { variant } = useExperiment({ experiment });
  const stripeLinks = useSelector(selectStripeLinks);
  const countdownAd = useSelector(getCountDownAd);
  const contactSection = useSelector(selectContactSection);
  const logoUrl = useSelector(selectLogoUrl);
  const channel = useSelector(selectChannel);
  const blackFridayIsActive = useSelector(selectBlackFridayStatus);
  const siteMode = useSelector(selectSiteMode);
  const isBGH = channel === 'BGH';
  const isBNO = channel === 'BNO';
  const isGOP = channel === 'GOP';

  const logo = () => {
    if (blackFridayIsActive) {
      if (isBNO) {
        return bnoLogoBlackFriday;
      }
      if (isBGH) {
        return logoBlackFriday;
      }
    }
    if (isGOP) {
      return siteMode === SiteMode.Company ? logoCompanyGOP : logoGOP;
    } else {
      return logoUrl;
    }
  };

  const smallLogo = () => {
    if (blackFridayIsActive && (isBGH || isBNO)) {
      return <SmallLogoBlackFriday />;
    }
    if (isGOP && siteMode === SiteMode.Company) {
      return <CompanyLogoSmall />;
    } else {
      return <SmallLogo />;
    }
  };

  const compactLogo = () => {
    if (blackFridayIsActive) {
      if (isBGH) {
        return <CompactLogoBlackFriday />;
      }
      if (isBNO) {
        return <SmallLogoBlackFriday />;
      }
    }
    if (isBGH) {
      return <CompactLogo />;
    }
    if (isGOP && siteMode === SiteMode.Company) {
      return <CompanyLogoSmall />;
    } else {
      return <SmallLogo />;
    }
  };

  const hasStripeLinks = hasElements(stripeLinks) && variant <= 0;

  return (
    <Container>
      <ThemedHeader
        logo={
          <Wrapper>
            <StyledLogo src={logo()} />
          </Wrapper>
        }
        hasStripeLinks={hasStripeLinks}
        smallLogo={<Wrapper $pp={channel === 'PP'}>{smallLogo()}</Wrapper>}
        compactLogo={<CompactWrapper $bgh={isBGH}>{compactLogo()}</CompactWrapper>}
        countdownAd={countdownAd}
        contactSection={contactSection}
        channel={channel}
        siteModeButton={isGOP ? <SiteModeButton /> : undefined}
        siteModeMobile={isGOP ? <MenuSiteModeBlock /> : undefined}
      />
    </Container>
  );
};
